/**
 * The vault tier of a `latest`-mode message page (plan A.9.1).
 *
 * Live store + vault are ONE timeline: the live rows, then the vault rows
 * older than the oldest live row (newer vault rows are copies of live ones).
 * `offset` counts from the newest row of that timeline, so the vault part of
 * a page starts `offset - liveTotal` rows past the live store and page N+1
 * continues exactly where page N ended. Rows come out ascending by `t`.
 */
import { extractSortT, type TieredMessage } from './merge-tiers';

export interface VaultTopUpParams<M extends TieredMessage> {
  /** This page's live rows, ascending. */
  live: M[];
  /** Live rows before paging (0 when the live tier is off). */
  liveTotal: number;
  offset: number;
  limit: number;
  /** Exclusive ms bounds of the caller's date window (cutoffDate / latestDate). */
  afterT?: number;
  beforeT?: number;
  /** `t` (seconds) of the chat's oldest live row — asked only when this page
   *  holds no live row but the live store has some. */
  oldestLiveT: () => Promise<number | undefined>;
  /** The vault read: the newest rows in `(afterT, beforeT)` (ms) after
   *  skipping `offset`, ascending (queryVaultMessages). */
  queryVault: (q: { afterT?: number; beforeT?: number; offset: number; limit: number }) => Promise<M[]>;
  /** How many vault rows the same window holds (countVaultMessages) — makes
   *  `total` the timeline's row count. */
  countVault?: (q: { afterT?: number; beforeT?: number }) => Promise<number>;
}

export interface VaultTopUpResult<M> {
  messages: M[];
  /** The vault filled its whole share of the page — older rows may follow. */
  vaultMayHaveMore: boolean;
  /** Rows in the whole timeline (live + vault), when the vault was read and
   *  `countVault` was given. */
  total?: number;
}

export async function vaultTopUp<M extends TieredMessage>(p: VaultTopUpParams<M>): Promise<VaultTopUpResult<M>> {
  const liveOnly = { messages: p.live, vaultMayHaveMore: false };
  const take = p.limit - p.live.length;
  // The page lies inside the live store (or is already full).
  if (take <= 0 || p.offset + p.limit <= p.liveTotal) return liveOnly;
  // The page reaches the start of the live store, so its first live row is
  // the oldest live row.
  const oldestT = p.live.length > 0 ? extractSortT(p.live[0]) : p.liveTotal > 0 ? await p.oldestLiveT() : undefined;
  // Never read the vault unbounded while the live store has rows it may copy.
  if (p.liveTotal > 0 && typeof oldestT !== 'number') return liveOnly;
  // Strict boundary: a vault-only row in the oldest live row's second is excluded.
  const boundary = typeof oldestT === 'number' ? oldestT * 1000 : undefined;
  const beforeT = boundary === undefined ? p.beforeT : Math.min(boundary, p.beforeT ?? Infinity);
  const vault = await p.queryVault({ afterT: p.afterT, beforeT, offset: Math.max(0, p.offset - p.liveTotal), limit: take });
  const total = p.countVault ? p.liveTotal + (await p.countVault({ afterT: p.afterT, beforeT })) : undefined;
  // Vault rows are strictly older than every live row.
  return { messages: [...vault, ...p.live], vaultMayHaveMore: vault.length === take, ...(total !== undefined ? { total } : {}) };
}

/** The browse args the vault tier reads (a waQueryMessagesTool args subset). */
export interface VaultBrowseArgs {
  chatId?: string;
  mode?: 'history' | 'latest';
  searchQuery?: string;
  searchQueryAny?: string[];
  messageTypes?: string[];
  mime?: string;
  sender?: string;
  hasMedia?: boolean;
  filenameContains?: string;
  cutoffDate?: string;
  latestDate?: string;
}

/**
 * The vault's exclusive ms window for a browse, or null when the vault must
 * not top it up: no chat, `history` mode (its offset counts from the live
 * store's oldest row), search or any browse filter (vault rows carry no
 * type / mime / sender / text index). cutoffDate / latestDate are inclusive
 * (filterMessagesByDateRange), hence the ±1 ms.
 */
export function vaultBrowseWindow(a: VaultBrowseArgs): { afterT?: number; beforeT?: number } | null {
  const filtered = !!(a.searchQuery || a.searchQueryAny?.length || a.messageTypes?.length
    || a.mime || a.sender || a.hasMedia !== undefined || a.filenameContains);
  if (!a.chatId || a.mode === 'history' || filtered) return null;
  const msOf = (d?: string) => (d ? new Date(d).getTime() : NaN);
  const cutoffMs = msOf(a.cutoffDate);
  const latestMs = msOf(a.latestDate);
  return {
    afterT: Number.isFinite(cutoffMs) ? cutoffMs - 1 : undefined,
    beforeT: Number.isFinite(latestMs) ? latestMs + 1 : undefined,
  };
}

/** The page's `paging` once the vault topped it up: when the vault filled its
 *  share an older page may exist, so `hasMoreInStore` is true (Team Inbox and
 *  /v1 request the next offset on it). */
export function withVaultPaging<P extends object>(paging: P | undefined, vaultMayHaveMore: boolean) {
  return vaultMayHaveMore ? { ...paging, hasMoreInStore: true as const } : paging;
}
